import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, formatErr } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  ShoppingBag, CheckCircle, XCircle, ClockClockwise, CurrencyDollar, Timer,
  FileXls, ArrowsClockwise, ChartBar, Package,
} from "@phosphor-icons/react";
import { fmtDate } from "@/lib/dateUtils";
import { SortableTh, useSortableRows } from "@/components/SortableTh";

const STATUS_LABELS = {
  taslak: "Taslak",
  teklif_bekliyor: "Teklif Bekliyor",
  onay_bekliyor: "Onay Bekliyor",
  onaylandi: "Onaylandı",
  reddedildi: "Reddedildi",
  siparis_verildi: "Sipariş Verildi",
};

const PRIORITY_LABELS = {
  dusuk: "Düşük",
  orta: "Orta",
  yuksek: "Yüksek",
  acil: "Acil",
};

const STATUS_COLORS = {
  taslak: "#94A3B8",
  teklif_bekliyor: "#F59E0B",
  onay_bekliyor: "#3B82F6",
  onaylandi: "#10B981",
  reddedildi: "#EF4444",
  siparis_verildi: "#8B5CF6",
};

const PRIORITY_COLORS = {
  dusuk: "#94A3B8",
  orta: "#3B82F6",
  yuksek: "#F59E0B",
  acil: "#EF4444",
};

const KpiCard = ({ icon: Icon, label, value, sub, tone = "navy" }) => {
  const bg = {
    navy: "bg-[#1B4776]",
    green: "bg-emerald-600",
    amber: "bg-amber-500",
    red: "bg-red-600",
    violet: "bg-violet-600",
    slate: "bg-slate-700",
  }[tone];
  return (
    <Card className="p-5 rounded-none sm:rounded-sm border-slate-200 shadow-sm bg-white">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 flex items-center justify-center ${bg}`}>
          <Icon size={18} weight="duotone" className="text-white" />
        </div>
      </div>
      <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-1">{label}</div>
      <div className="text-3xl font-black text-slate-900 font-heading">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </Card>
  );
};

const fmtTL = (n) =>
  new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 })
    .format(Number(n) || 0);

const fmtShortTL = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M ₺`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K ₺`;
  return `${v.toFixed(0)} ₺`;
};

export default function PurchaseReports() {
  const { sites } = useSites();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    date_from: "",
    date_to: "",
    site_id: "all",
    priority: "all",
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.site_id && filters.site_id !== "all") params.site_id = filters.site_id;
      if (filters.priority && filters.priority !== "all") params.priority = filters.priority;
      const { data } = await axios.get(`${API}/purchase/reports/summary`, { params });
      setData(data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.site_id, filters.priority]);
  useEffect(() => {
    const t = setTimeout(load, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [filters.date_from, filters.date_to]);

  const exportXlsx = () => {
    const url = new URL(`${API}/purchase/reports/export.xlsx`);
    if (filters.date_from) url.searchParams.set("date_from", filters.date_from);
    if (filters.date_to) url.searchParams.set("date_to", filters.date_to);
    if (filters.site_id && filters.site_id !== "all") url.searchParams.set("site_id", filters.site_id);
    if (filters.priority && filters.priority !== "all") url.searchParams.set("priority", filters.priority);
    window.open(url.toString(), "_blank", "noopener,noreferrer");
  };

  const kpi = data?.kpi || { total: 0, open: 0, approved: 0, rejected: 0, total_value: 0, avg_approval_hours: 0 };

  const statusRows = useMemo(() =>
    (data?.by_status || []).map(x => ({ ...x, label: STATUS_LABELS[x.name] || x.name })),
    [data]);

  const priorityRows = useMemo(() =>
    (data?.by_priority || []).map(x => ({ ...x, label: PRIORITY_LABELS[x.name] || x.name })),
    [data]);

  const { sort, toggleSort, sortedRows: detailSorted } = useSortableRows(data?.detail || [], {
    defaultKey: "created_at", defaultDir: "desc",
    getters: {
      code: (r) => r.code || "",
      created_at: (r) => r.created_at || "",
      site_name: (r) => r.site_name || "",
      material_name: (r) => r.material_name || "",
      quantity: (r) => Number(r.quantity) || 0,
      priority: (r) => r.priority || "",
      status: (r) => r.status || "",
      offers_count: (r) => Number(r.offers_count) || 0,
      selected_supplier: (r) => r.selected_supplier || "",
      total_value: (r) => Number(r.total_value) || 0,
    },
  });

  return (
    <Shell
      title="Satın Alma Raporları"
      subtitle="Satın Alma Yönetimi"
      actions={
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={load}
            data-testid="purchase-reports-refresh"
            className="rounded-none sm:rounded-sm"
          >
            <ArrowsClockwise size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Yenile
          </Button>
          <Button
            onClick={exportXlsx}
            data-testid="purchase-reports-xlsx"
            className="rounded-none sm:rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <FileXls size={14} className="mr-1.5" /> Excel İndir
          </Button>
        </div>
      }
    >
      {/* Filtreler */}
      <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm bg-white mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Başlangıç</Label>
            <Input type="date" value={filters.date_from}
                   onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))}
                   className="mt-1 rounded-none sm:rounded-sm" data-testid="filter-date-from" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Bitiş</Label>
            <Input type="date" value={filters.date_to}
                   onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))}
                   className="mt-1 rounded-none sm:rounded-sm" data-testid="filter-date-to" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Şantiye</Label>
            <Select value={filters.site_id} onValueChange={v => setFilters(f => ({ ...f, site_id: v }))}>
              <SelectTrigger className="mt-1 rounded-none sm:rounded-sm" data-testid="filter-site"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Öncelik</Label>
            <Select value={filters.priority} onValueChange={v => setFilters(f => ({ ...f, priority: v }))}>
              <SelectTrigger className="mt-1 rounded-none sm:rounded-sm" data-testid="filter-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="dusuk">Düşük</SelectItem>
                <SelectItem value="orta">Orta</SelectItem>
                <SelectItem value="yuksek">Yüksek</SelectItem>
                <SelectItem value="acil">Acil</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* KPI Kartları */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <KpiCard icon={ShoppingBag} label="Toplam Talep" value={kpi.total} tone="navy" />
        <KpiCard icon={ClockClockwise} label="Bekleyen" value={kpi.open} sub="Teklif+Onay" tone="amber" />
        <KpiCard icon={CheckCircle} label="Onaylanan" value={kpi.approved} sub="Sipariş dahil" tone="green" />
        <KpiCard icon={XCircle} label="Reddedilen" value={kpi.rejected} tone="red" />
        <KpiCard icon={CurrencyDollar} label="Toplam Değer" value={fmtShortTL(kpi.total_value)}
                 sub="Seçilen tekliflerden" tone="violet" />
        <KpiCard icon={Timer} label="Ort. Onay Süresi"
                 value={kpi.avg_approval_hours >= 24
                   ? `${(kpi.avg_approval_hours / 24).toFixed(1)} gün`
                   : `${kpi.avg_approval_hours.toFixed(1)} sa`}
                 tone="slate" />
      </div>

      {/* Grafikler */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* Durum Dağılımı - Pie */}
        <Card className="p-5 rounded-none sm:rounded-sm border-slate-200 shadow-sm bg-white">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 bg-[#1B4776] flex items-center justify-center">
              <ChartBar size={14} weight="duotone" className="text-[#95C11F]" />
            </div>
            <div className="text-sm font-bold text-slate-900 font-heading">Durum Dağılımı</div>
          </div>
          {statusRows.length === 0 ? (
            <div className="text-center py-16 text-sm text-slate-400">Veri yok</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={statusRows} dataKey="value" nameKey="label" cx="50%" cy="50%"
                     innerRadius={50} outerRadius={90} paddingAngle={2}>
                  {statusRows.map((r, i) => (
                    <Cell key={i} fill={STATUS_COLORS[r.name] || "#64748B"} />
                  ))}
                </Pie>
                <Legend verticalAlign="bottom" iconType="square" wrapperStyle={{ fontSize: 11 }} />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Aylık Trend */}
        <Card className="p-5 rounded-none sm:rounded-sm border-slate-200 shadow-sm bg-white">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 bg-[#1B4776] flex items-center justify-center">
              <ChartBar size={14} weight="duotone" className="text-[#95C11F]" />
            </div>
            <div className="text-sm font-bold text-slate-900 font-heading">Aylık Talep Trendi</div>
          </div>
          {(data?.monthly || []).length === 0 ? (
            <div className="text-center py-16 text-sm text-slate-400">Veri yok</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#1B4776" name="Talep Adedi" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Şantiye Bazlı */}
        <Card className="p-5 rounded-none sm:rounded-sm border-slate-200 shadow-sm bg-white">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 bg-[#1B4776] flex items-center justify-center">
              <Package size={14} weight="duotone" className="text-[#95C11F]" />
            </div>
            <div className="text-sm font-bold text-slate-900 font-heading">Şantiye Bazında Talepler</div>
          </div>
          {(data?.by_site || []).length === 0 ? (
            <div className="text-center py-16 text-sm text-slate-400">Veri yok</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.by_site} layout="vertical" margin={{ left: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} />
                <Tooltip />
                <Bar dataKey="value" fill="#95C11F" name="Talep Adedi" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Top Firmalar (Onaylı Değer) */}
        <Card className="p-5 rounded-none sm:rounded-sm border-slate-200 shadow-sm bg-white">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 bg-[#1B4776] flex items-center justify-center">
              <CurrencyDollar size={14} weight="duotone" className="text-[#95C11F]" />
            </div>
            <div className="text-sm font-bold text-slate-900 font-heading">
              En Çok Onaylanan Firmalar (Tutar)
            </div>
          </div>
          {(data?.top_suppliers || []).length === 0 ? (
            <div className="text-center py-16 text-sm text-slate-400">Henüz onaylı teklif yok</div>
          ) : (
            <div className="space-y-2">
              {data.top_suppliers.map((s, i) => {
                const max = data.top_suppliers[0].value || 1;
                const pct = (s.value / max) * 100;
                return (
                  <div key={i} data-testid={`top-supplier-${i}`}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium text-slate-700 truncate">{s.name}</span>
                      <span className="font-mono font-bold text-slate-900">{fmtTL(s.value)}</span>
                    </div>
                    <div className="h-1.5 bg-slate-100">
                      <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Detay Tablosu */}
      <Card className="rounded-none sm:rounded-sm border-slate-200 shadow-sm bg-white overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-sm font-bold text-slate-900 font-heading">Talep Detayı</div>
            <div className="text-xs text-slate-500">{(data?.detail || []).length} kayıt</div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-slate-200 bg-slate-50">
                <SortableTh label="Kod" k="code" sort={sort} onSort={toggleSort} className="text-left py-2.5 px-3" />
                <SortableTh label="Tarih" k="created_at" sort={sort} onSort={toggleSort} className="text-left py-2.5 px-3" />
                <SortableTh label="Şantiye" k="site_name" sort={sort} onSort={toggleSort} className="text-left py-2.5 px-3" />
                <SortableTh label="Malzeme" k="material_name" sort={sort} onSort={toggleSort} className="text-left py-2.5 px-3" />
                <SortableTh label="Miktar" k="quantity" sort={sort} onSort={toggleSort} className="text-right py-2.5 px-3" align="right" />
                <SortableTh label="Öncelik" k="priority" sort={sort} onSort={toggleSort} className="text-left py-2.5 px-3" />
                <SortableTh label="Durum" k="status" sort={sort} onSort={toggleSort} className="text-left py-2.5 px-3" />
                <SortableTh label="Teklif" k="offers_count" sort={sort} onSort={toggleSort} className="text-right py-2.5 px-3" align="right" />
                <SortableTh label="Seçilen Firma" k="selected_supplier" sort={sort} onSort={toggleSort} className="text-left py-2.5 px-3" />
                <SortableTh label="Tutar" k="total_value" sort={sort} onSort={toggleSort} className="text-right py-2.5 px-3" align="right" />
              </tr>
            </thead>
            <tbody>
              {detailSorted.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-12 text-sm text-slate-400">Kayıt yok</td>
                </tr>
              ) : (
                detailSorted.map((d, i) => {
                  const stColor = STATUS_COLORS[d.status] || "#64748B";
                  const prColor = PRIORITY_COLORS[d.priority] || "#64748B";
                  return (
                    <tr key={d.id} className={`border-b border-slate-100 hover:bg-slate-50/50 ${i % 2 ? "bg-slate-50/30" : ""}`} data-testid={`report-row-${i}`}>
                      <td className="py-2 px-3 font-mono text-xs font-bold text-slate-900">{d.code}</td>
                      <td className="py-2 px-3 text-slate-600 text-xs">{fmtDate(d.created_at)}</td>
                      <td className="py-2 px-3 text-slate-700 text-xs">{d.site_name}</td>
                      <td className="py-2 px-3 text-slate-700 max-w-xs truncate">{d.material_name}</td>
                      <td className="py-2 px-3 text-right font-mono text-slate-700">{d.quantity} {d.unit}</td>
                      <td className="py-2 px-3">
                        <span className="inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white"
                              style={{ backgroundColor: prColor }}>
                          {PRIORITY_LABELS[d.priority] || d.priority}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        <span className="inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white"
                              style={{ backgroundColor: stColor }}>
                          {STATUS_LABELS[d.status] || d.status}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-right font-mono text-slate-600">{d.offers_count}</td>
                      <td className="py-2 px-3 text-slate-700 text-xs">{d.selected_supplier || "-"}</td>
                      <td className="py-2 px-3 text-right font-mono font-bold text-slate-900">
                        {d.total_value ? fmtTL(d.total_value) : "-"}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </Shell>
  );
}
